import React from "react";
import { format } from "date-fns";
import { Box, Button, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import EmailIcon from "@mui/icons-material/EmailOutlined";

import { useAlert } from "@APP/hooks";
import { AppState } from "@APP/redux";
import { formatCurrency } from "@APP/utils";
import { downloadInvoice } from "@APP/utils/invoice";
import { ERP_ID, ATTACHEMENTS } from "@APP/types";
import { getModalCommonButton } from "@APP/views/DeclineRTPScreen/getModalContent";
import { MOBILE_CARD_PADDING, MOBILE_CARD_RADIUS, MOBILE_FONT } from "@APP/styles/mobile";

import { getModalMessageDownloadInvoiceFailure } from "./getModalContent";

interface Props {
  rtpState: AppState["rtp"];
}

/**
 * Lloyds (LBG) variant.
 *
 * Both mobile and desktop use the same two left-aligned white cards: the
 * request itself (sender, amount, reference and a "View details" link to the
 * invoice), then the contact row. On both breakpoints the cards span the full
 * content column, so their edges line up with the page title above and the
 * actions below them.
 */
// `sx` rather than `makeStyles` wherever the rule overrides a Typography
// variant or a MuiButton theme override — JSS and emotion classes carry equal
// specificity, so a JSS rule can lose depending on stylesheet order. Sizes step
// up from the `sm` breakpoint for the desktop layout.
const BODY_SX = { fontSize: { xs: `${MOBILE_FONT.body}px`, sm: "16px" } };

const AMOUNT_SX = { fontSize: { xs: `${MOBILE_FONT.amount}px`, sm: "30px" }, my: "4px" };

const VIEW_DETAILS_SX = {
  minWidth: 0,
  p: 0,
  fontSize: { xs: `${MOBILE_FONT.body}px`, sm: "16px" },
  fontWeight: 400,
  textDecoration: "underline",
  whiteSpace: "nowrap",
  "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
};

const CONTACT_BUTTON_SX = {
  width: "100%",
  minWidth: 0,
  p: 0,
  fontSize: { xs: `${MOBILE_FONT.body}px`, sm: "16px" },
  fontWeight: 400,
  justifyContent: "flex-start",
  "& .MuiButton-startIcon svg": { fontSize: { xs: 18, sm: 20 } },
  "&:hover": { backgroundColor: "transparent" },
};

const useStyles = makeStyles((theme) => ({
  summary: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
  },
  card: {
    width: "100%",
    textAlign: "left",
    backgroundColor: theme.palette.background.paper,
    borderRadius: MOBILE_CARD_RADIUS,
    padding: MOBILE_CARD_PADDING,
    [theme.breakpoints.up("sm")]: {
      borderRadius: 12,
      padding: 24,
    },
  },
  referenceRow: {
    // The design groups the reference and the "View details" link together on
    // the left with a comfortable gap, rather than spreading them edge-to-edge.
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "baseline",
    gap: theme.spacing(3),
  },
  contactCard: {
    // The design leaves a clear gap between the payment card and the contact
    // card — 16px on mobile, 36px on desktop per Figma.
    marginTop: 16,
    [theme.breakpoints.up("sm")]: {
      marginTop: 36,
      // A single-line row, so it keeps the tighter mobile padding on desktop
      // rather than the payment card's 24px — the Figma row is ~54px tall.
      padding: "16px 20px",
    },
  },
  instalments: {
    width: "100%",
    textAlign: "left",
  },
}));

const RTPSummary: React.FC<Props> = ({ rtpState }) => {
  const { rtp, rtpData, senderData } = rtpState;
  const { alert } = useAlert();
  const styles = useStyles();

  const { entityDetails } = rtpData?.payable || {};
  const reference = rtpData?.payable?.reference;

  const isInternal = entityDetails?.source === ERP_ID.INTERNAL;
  const isNative = rtp?.receivable.features?.includes("Native");
  const invoiceLink = rtpData?.payable?.attachments?.find(
    (item) => item.name === ATTACHEMENTS.RECEIVABLE_PDF && item.contentType === "application/pdf",
  )?.uri;

  async function handleDownloadInvoice() {
    if (!invoiceLink) return;

    const downloaded = await downloadInvoice(invoiceLink);
    if (!downloaded) {
      alert("", getModalMessageDownloadInvoiceFailure(), getModalCommonButton());
    }
  }

  // Download button should be displayed in case of external invoice or internal native invoice:
  const shouldDisplayDownloadLink = invoiceLink && (!isInternal || isNative) && !rtp?.isBulkRtp;

  const senderName = senderData?.businessContact?.name ?? senderData?.name;
  const contactHref = `mailto:${
    senderData?.businessContact?.email ?? senderData?.companyInfo.email
  }`;

  const instalments = rtp?.standingOrder ? (
    <Box mt={2} mb={1} display="flex" flexDirection="column" alignItems="flex-start">
      <Typography gutterBottom id="rtpSummeryPayment">
        Payment to be taken in the below instalments
      </Typography>
      <Box mt={1} width="100%" display="flex" flexDirection="column" className={styles.instalments}>
        <Box display="flex" width="100%" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" gutterBottom id="rtpSummeryPaymentLabel">
            Payment Amount
          </Typography>
          <Typography gutterBottom id="rtpSummeryFirstPaymentAmt">
            {formatCurrency(rtp!.standingOrder!.firstPaymentAmount)}
          </Typography>
        </Box>
        {rtp!.standingOrder!.finalPaymentAmount && (
          <Box display="flex" width="100%" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" gutterBottom id="rtpSummeryFinalPaymentLabel">
              Final Payment
            </Typography>
            <Typography gutterBottom id="rtpSummeryFinalPaymentAmy">
              {formatCurrency(rtp!.standingOrder!.finalPaymentAmount)}
            </Typography>
          </Box>
        )}
        <Box display="flex" width="100%" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" gutterBottom id="rtpSummeryFrequencyLabel">
            Frequency
          </Typography>
          <Typography gutterBottom id="rtpSummeryFreq">
            {rtp!.standingOrder!.frequency}
          </Typography>
        </Box>
        <Box display="flex" width="100%" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" gutterBottom id="rtpSummeryOccurencesLabel">
            Occurrences
          </Typography>
          <Typography gutterBottom id="rtpSummeryNoOfPayments">
            {rtp!.standingOrder!.numberOfPayments}
          </Typography>
        </Box>
        <Box display="flex" width="100%" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" gutterBottom id="rtpSummeryFirstPayDateLabel">
            First payment date
          </Typography>
          <Typography gutterBottom id="rtpSummeryFirstPaymentDate">
            {format(new Date(rtp!.standingOrder!.firstPaymentDate!), "yyyy-MM-dd")}
          </Typography>
        </Box>
      </Box>
    </Box>
  ) : null;

  return (
    <Box className={styles.summary} id="rtpSummeryBox">
      <Box className={styles.card}>
        <Typography sx={BODY_SX} id="rtpSummeryTypogrophy">
          {senderName} has requested
        </Typography>
        <Typography component="p" variant="h4" sx={AMOUNT_SX} id="rtpSummeryAmount">
          {formatCurrency(rtpData?.paymentDetails.amount)}
        </Typography>
        <Box className={styles.referenceRow}>
          <Typography sx={BODY_SX} id="rtpSummeryInvoiceTypo">
            {isInternal ? "For" : "For invoice"}
            {reference ? <span id="rtpSummeryRef">&nbsp;{reference}</span> : null}
          </Typography>
          {shouldDisplayDownloadLink && (
            <Button
              sx={VIEW_DETAILS_SX}
              disableRipple
              onClick={handleDownloadInvoice}
              id="rtpSummeryDownloadInvoiceButton">
              View details
            </Button>
          )}
        </Box>
        {/* The design ends the card at the reference line — the invoice
            line-item description is not shown on this screen. */}
        {instalments}
      </Box>
      <Box className={`${styles.card} ${styles.contactCard}`}>
        <Button
          sx={CONTACT_BUTTON_SX}
          disableRipple
          href={contactHref}
          target="_blank"
          rel="noopener noreferrer"
          id="rtpSummeryContactButton"
          startIcon={<EmailIcon />}>
          Contact {senderName}
        </Button>
      </Box>
    </Box>
  );
};

export default RTPSummary;
